import React, { useEffect, SVGProps } from 'react';

import PropTypes from 'prop-types';

import { StylableComponent, stylePropType } from '@adsk/alloy-react-helpers';

import { IconType, ICON_ALIAS_TYPES, ICON_TYPES, ICONS } from './consts';

type SVGSVGElementProps = SVGProps<SVGSVGElement>;

export type IconProps = StylableComponent<SVGSVGElement, SVGSVGElementProps> & {
  className?: string;
  size?: number;
  type?: IconType;
  children?: SVGSVGElementProps['children'];
};

/**
 * A component for rendering single color icons as inline SVGs.
 */
const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ style, className, children, size = 24, type, ...props }, ref) => {
    const aliasType =
      type && ICON_ALIAS_TYPES[type as keyof typeof ICON_ALIAS_TYPES];
    useEffect(() => {
      if (aliasType) {
        console.warn(
          `Icon type "${type}" is deprecated, please update to type, "${aliasType}".`
        );
      }
    }, [type, aliasType]);
    const finalType = aliasType || type;

    return (
      <svg
        ref={ref}
        height={size}
        width={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        css={style ? [style] : undefined}
        className={className}
        data-testid={type}
        {...props}
      >
        {children ? (
          children
        ) : (
          <path
            d={ICONS[finalType as keyof typeof ICONS]}
            fill="currentColor"
          />
        )}
      </svg>
    );
  }
);

Icon.displayName = 'Icon';

Icon.defaultProps = {
  size: 24,
};

Icon.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Icon content, if rendering a custom SVG not listed in included types */
  children: PropTypes.node,
  /** Square size of icon (in pixels, defaults to 24) */
  size: PropTypes.number,
  /** Icon type */
  type: PropTypes.oneOf<IconType>(Object.values(ICON_TYPES)),
};

export default Object.assign(Icon, { TYPES: ICON_TYPES });
